import JobApplication, { type IJobApplication } from './../JobApplication';
import jobApplicationService from '../../../services/jobApplicationService';
import { STATE_ERROR, STATE_LOADED } from '../../state';
import { logError } from '../../../utils/logError';
import type { Prospect } from 'src/graphql/types';
import { mapApiResponseToStore } from './mapApiResponseToStore';
import type { Bucket, ProspectFilters } from '../types';
import { PER_PAGE } from '../../../constants/numPerPage';
import isFeatureEnabled from '../../../modules/featureToggle/isFeatureEnabled';
import { FEATURE_SEARCH_API } from '../../../modules/featureToggle';
import type {
  FilterCounts,
  BucketStatusCounts,
} from '../../../graphql/fetchProspectsWithPaginationByJobId';
import type { Language } from '@seek/melways-sites';

interface BucketStatsStore {
  state: string;
  bucketStats: BucketStatusCounts;
}

interface JobApplicationListResult {
  jobApplications: IJobApplication[];
  state: string;
  preFilterTotal: number;
  total: number;
  filterCounts: FilterCounts;
  bucketStatsStore: BucketStatsStore;
}

export const getSearchTerm = (searchText: string) =>
  isFeatureEnabled(FEATURE_SEARCH_API)
    ? searchText
    : searchText
        .split(' ')
        .filter((terms) => terms.length > 2)
        .join(' ');

export const getJobApplicationList = async (
  jobId: number,
  searchText: string,
  language: Language,
  displayCountry: string,
  bucket: Bucket,
  sortBy: string, // TODO_TYPES
  pageNumber: number = 1,
  selectedJobApplicationIds: string[] = [],
  filters?: ProspectFilters,
): Promise<JobApplicationListResult> => {
  try {
    const searchQuery = getSearchTerm(searchText);
    const jobApplicationServiceFetchResult = await jobApplicationService.fetch(
      jobId,
      isFeatureEnabled(FEATURE_SEARCH_API) ? searchText : searchQuery,
      language,
      displayCountry,
      bucket as any,
      sortBy,
      pageNumber,
      filters,
    );
    const offset = (pageNumber - 1) * PER_PAGE;

    const jobApplications = jobApplicationServiceFetchResult.nodes.map(
      (jobApplication: Prospect, index: number) =>
        JobApplication.create({
          ...mapApiResponseToStore(jobApplication),
          positionInList: offset + index + 1,
          selected: selectedJobApplicationIds.includes(jobApplication.id),
        }),
    );

    return {
      jobApplications,
      preFilterTotal:
        jobApplicationServiceFetchResult.pageInfo?.preFilterTotal ?? 0,
      total: jobApplicationServiceFetchResult.pageInfo?.total ?? 0,
      state: STATE_LOADED,
      filterCounts: jobApplicationServiceFetchResult.filterCounts ?? {},
      bucketStatsStore: {
        state: STATE_LOADED,
        bucketStats: jobApplicationServiceFetchResult.bucketCounts ?? {},
      },
    };
  } catch (error: any) {
    logError(
      {
        errorCode: 'GET_JOB_APPLICATION',
        description: 'Error attempting: jobApplicationService.fetch',
      },
      {
        error,
      },
    );
    return {
      jobApplications: [],
      preFilterTotal: 0,
      total: 0,
      state: STATE_ERROR,
      filterCounts: {},
      bucketStatsStore: {
        state: STATE_LOADED,
        bucketStats: {},
      },
    };
  }
};
